import { createSelector } from 'reselect'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import { countExperienceTicketParticipants } from 'checkout/lib/utils/experiences/experienceTicketParticipants'
import { groupBy } from 'lib/array/arrayUtils'

/**
 * Occupancy equivalent of the experience traveller forms, for the insurance
 * quote side of checkout.
 *
 * svc-traveller expands each experience item's tickets into participant forms
 * (same expansion as getExperiencesSchemaRequestItems) keyed adult-N/child-N,
 * and forms with the same key are shared across items — so across items the
 * form set is the max per age group, not the sum. This selector mirrors that
 * union so quote occupancy can be reconciled with the forms the order submits.
 *
 * Loyalty lounge passes are separate cart items, but the traveller form schema
 * sends them as experience schema items too — grouped by offer with one (adult)
 * participant form per pass (getLoyaltyLoungePassSchemaRequestItems) — so they
 * join the same union.
 *
 * Returns undefined when the cart has no bookable experience participants.
 */
const getExperienceParticipantOccupants = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getLoyaltyLoungePassItemViews(state),
  (experienceItemsView, loungePassItemViews): App.Occupants | undefined => {
    const experienceCounts = experienceItemsView.data
      // same items the traveller form schema requests forms for
      .filter(item => item.type !== 'addon' && item.isValid)
      .map(item => countExperienceTicketParticipants(item.ticketSummaryViews))

    const loungePassCounts = Array.from(
      groupBy(loungePassItemViews.data, view => view.item.experienceId).values(),
      passes => ({ adults: passes.length, children: 0 }),
    )

    const merged = [...experienceCounts, ...loungePassCounts].reduce(
      (max, counts) => ({
        adults: Math.max(max.adults, counts.adults),
        children: Math.max(max.children, counts.children),
      }),
      { adults: 0, children: 0 },
    )

    if (merged.adults === 0 && merged.children === 0) return undefined

    return {
      adults: merged.adults,
      children: merged.children,
      infants: 0,
      childrenAge: [],
    }
  },
)

export default getExperienceParticipantOccupants
